import React from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from './components/ui/sonner';
import { AuthProvider, useAuth } from './context/AuthContext';
import SidebarLayout from './components/SidebarLayout';
import EmployeeLayout from './components/EmployeeLayout';

// Admin/Staff Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import LoanFilesPage from './pages/LoanFilesPage';
import CustomersPage from './pages/CustomersPage';
import EmployeesPage from './pages/EmployeesPage';
import BankAgentsPage from './pages/BankAgentsPage';
import AttendancePage from './pages/AttendancePage';
import SalaryPage from './pages/SalaryPage';
import SettingsPage from './pages/SettingsPage';
import EnquiriesPage from './pages/EnquiriesPage';
import ExpensesPage from './pages/ExpensesPage';
import PaymentsPage from './pages/PaymentsPage';
import FinancialsPage from './pages/FinancialsPage';
import NotificationHistoryPage from './pages/NotificationHistoryPage';

// Public Pages
import LandingPage from './pages/LandingPage';

// Customer Portal Pages
import PortalAuthPage from './pages/PortalAuthPage';
import PortalDashboard from './pages/PortalDashboard';
import PortalDocuments from './pages/PortalDocuments';

// Employee Portal Pages
import EmployeeLoginPage from './pages/EmployeeLoginPage';
import EmployeeDashboard from './pages/EmployeeDashboard';
import EmployeeAttendancePage from './pages/EmployeeAttendancePage';
import EmployeeSalaryPage from './pages/EmployeeSalaryPage';
import EmployeeSettingsPage from './pages/EmployeeSettingsPage';
import EmployeeLeavePage from './pages/EmployeeLeavePage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import AdminRecoveryPage from './pages/AdminRecoveryPage';

// Protected Route for Admin/Staff
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return <SidebarLayout>{children}</SidebarLayout>;
};

// Admin-only route – employees are redirected to /dashboard
const AdminOnlyRoute = ({ children }) => {
  const { isAuthenticated, loading, isAdmin } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return <SidebarLayout>{children}</SidebarLayout>;
};

// Public Route (redirect to dashboard if logged in as staff)
const StaffPublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin h-8 w-8 border-4 border-brand-navy border-t-transparent rounded-full"></div>
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

// Protected Route for Employee Portal
const ProtectedEmployeeRoute = ({ children }) => {
  const token = localStorage.getItem('employeeToken');
  const user = localStorage.getItem('employeeUser');
  
  if (!token || !user) {
    return <Navigate to="/employee/login" replace />;
  }
  
  const userData = JSON.parse(user);
  if (userData.role !== 'employee') {
    localStorage.removeItem('employeeToken');
    localStorage.removeItem('employeeUser');
    return <Navigate to="/employee/login" replace />;
  }
  
  return <EmployeeLayout>{children}</EmployeeLayout>;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Landing Page */}
      <Route path="/" element={<LandingPage />} />
      
      {/* Staff/Admin Login */}
      <Route path="/login" element={
        <StaffPublicRoute>
          <LoginPage />
        </StaffPublicRoute>
      } />
      
      {/* Forgot Password */}
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      
      {/* Admin Recovery - Secret page for emergency */}
      <Route path="/admin-recovery" element={<AdminRecoveryPage />} />
      
      {/* Admin/Staff Protected Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/enquiries" element={
        <ProtectedRoute>
          <EnquiriesPage />
        </ProtectedRoute>
      } />
      <Route path="/loan-files" element={
        <ProtectedRoute>
          <LoanFilesPage />
        </ProtectedRoute>
      } />
      <Route path="/customers" element={
        <ProtectedRoute>
          <CustomersPage />
        </ProtectedRoute>
      } />
      <Route path="/employees" element={
        <AdminOnlyRoute>
          <EmployeesPage />
        </AdminOnlyRoute>
      } />
      <Route path="/bank-agents" element={
        <ProtectedRoute>
          <BankAgentsPage />
        </ProtectedRoute>
      } />
      <Route path="/attendance" element={
        <AdminOnlyRoute>
          <AttendancePage />
        </AdminOnlyRoute>
      } />
      <Route path="/salary" element={
        <AdminOnlyRoute>
          <SalaryPage />
        </AdminOnlyRoute>
      } />
      <Route path="/expenses" element={
        <AdminOnlyRoute>
          <ExpensesPage />
        </AdminOnlyRoute>
      } />
      <Route path="/payments" element={
        <AdminOnlyRoute>
          <PaymentsPage />
        </AdminOnlyRoute>
      } />
      <Route path="/financials" element={
        <AdminOnlyRoute>
          <FinancialsPage />
        </AdminOnlyRoute>
      } />
      <Route path="/notification-history" element={
        <ProtectedRoute>
          <NotificationHistoryPage />
        </ProtectedRoute>
      } />
      <Route path="/settings" element={
        <ProtectedRoute>
          <SettingsPage />
        </ProtectedRoute>
      } />
      
      {/* Customer Portal Routes */}
      <Route path="/portal/login" element={<PortalAuthPage />} />
      <Route path="/portal/dashboard" element={<PortalDashboard />} />
      <Route path="/portal/documents" element={<PortalDocuments />} />
      
      {/* Employee Portal Routes */}
      <Route path="/employee/login" element={<EmployeeLoginPage />} />
      <Route path="/employee/dashboard" element={
        <ProtectedEmployeeRoute>
          <EmployeeDashboard />
        </ProtectedEmployeeRoute>
      } />
      <Route path="/employee/attendance" element={
        <ProtectedEmployeeRoute>
          <EmployeeAttendancePage />
        </ProtectedEmployeeRoute>
      } />
      <Route path="/employee/salary" element={
        <ProtectedEmployeeRoute>
          <EmployeeSalaryPage />
        </ProtectedEmployeeRoute>
      } />
      <Route path="/employee/settings" element={
        <ProtectedEmployeeRoute>
          <EmployeeSettingsPage />
        </ProtectedEmployeeRoute>
      } />
      <Route path="/employee/leave" element={
        <ProtectedEmployeeRoute>
          <EmployeeLeavePage />
        </ProtectedEmployeeRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
